import {
  discoveryApiRef,
  fetchApiRef,
  githubAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Alert from '@material-ui/lab/Alert';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CatalogBoardSummary, JiraProjectOption } from './jiraBoardTypes';

type BatchSwapBoardMappingDialogProps = {
  open: boolean;
  onClose: () => void;
  catalogSummaries: CatalogBoardSummary[];
  jiraProjects: JiraProjectOption[];
};

type JobDetails = {
  id: string;
  title: string;
};

type QueueResult = {
  job: JobDetails;
  skipped: Array<{ entityRef: string; message: string }>;
};

export function BatchSwapBoardMappingDialog({
  open,
  onClose,
  catalogSummaries,
  jiraProjects,
}: BatchSwapBoardMappingDialogProps) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const githubAuth = useApi(githubAuthApiRef);

  const [sourceProjectKey, setSourceProjectKey] = useState('');
  const [targetProjectKey, setTargetProjectKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [result, setResult] = useState<QueueResult | undefined>();

  const sortedCatalogSources = useMemo(
    () => [...catalogSummaries].sort((a, b) => a.projectKey.localeCompare(b.projectKey)),
    [catalogSummaries],
  );

  const sortedTargetProjects = useMemo(
    () =>
      [...jiraProjects]
        .filter(
          project => project.key.trim().toUpperCase() !== sourceProjectKey.trim().toUpperCase(),
        )
        .sort((a, b) => a.key.localeCompare(b.key)),
    [jiraProjects, sourceProjectKey],
  );

  const selectedSummary = useMemo(
    () =>
      catalogSummaries.find(
        summary =>
          summary.projectKey.trim().toUpperCase() === sourceProjectKey.trim().toUpperCase(),
      ),
    [catalogSummaries, sourceProjectKey],
  );

  const updatableEntityRefs = useMemo(
    () => selectedSummary?.updatableEntityRefs ?? [],
    [selectedSummary],
  );
  const skippedCount = Math.max(
    0,
    (selectedSummary?.catalogComponentCount ?? 0) - updatableEntityRefs.length,
  );

  const resetState = useCallback(() => {
    setSourceProjectKey('');
    setTargetProjectKey('');
    setSubmitting(false);
    setError(undefined);
    setResult(undefined);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleDialogClose = useCallback(() => {
    if (!submitting) {
      handleClose();
    }
  }, [handleClose, submitting]);

  const handleSourceChange = useCallback((nextSource: string) => {
    setSourceProjectKey(nextSource);
    setTargetProjectKey(current => (current === nextSource ? '' : current));
    setError(undefined);
    setResult(undefined);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!sourceProjectKey) {
      setError('Select a catalog Jira board to replace');
      return;
    }
    if (!targetProjectKey) {
      setError('Select a target Jira project');
      return;
    }
    if (updatableEntityRefs.length === 0) {
      setError('No components with github.com/project-slug are mapped to the selected board');
      return;
    }

    setSubmitting(true);
    setError(undefined);
    setResult(undefined);

    try {
      const [batchEditorBaseUrl, githubToken] = await Promise.all([
        discoveryApi.getBaseUrl('batch-editor'),
        githubAuth.getAccessToken(['repo', 'read:user', 'user:email']),
      ]);

      const response = await fetchApi.fetch(
        `${batchEditorBaseUrl}/jobs/jira-board-mapping/batch`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-GitHub-Authorization': `Bearer ${githubToken}`,
          },
          body: JSON.stringify({
            entityRefs: updatableEntityRefs,
            projectKey: targetProjectKey,
            removeProjectKeys: [sourceProjectKey],
          }),
        },
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Request failed with ${response.status}`);
      }

      const payload = (await response.json()) as QueueResult;
      setResult(payload);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setSubmitting(false);
    }
  }, [
    discoveryApi,
    fetchApi,
    githubAuth,
    sourceProjectKey,
    targetProjectKey,
    updatableEntityRefs,
  ]);

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Batch swap Jira board mapping</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Replace every catalog component mapped to a Jira board in Backstage with a valid active
          Jira project. All repositories are queued as one Batch Editor job so approval and merge
          happen once for the whole swap.
        </Typography>

        {error ? (
          <Box style={{ marginBottom: 16 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        {result ? (
          <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
            <Alert severity={result.skipped.length > 0 ? 'warning' : 'success'}>
              Queued batch job {result.job.id} for {result.job.title}
              {result.skipped.length > 0 ? `; ${result.skipped.length} component(s) skipped` : ''}.
            </Alert>
            <Typography variant="body2">
              Open{' '}
              <Link
                component={RouterLink}
                to={`/batch-editor?jobId=${encodeURIComponent(result.job.id)}`}
              >
                Batch Editor
              </Link>{' '}
              to review, approve once, and merge when ready.
            </Typography>
            {result.skipped.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Skipped components
                </Typography>
                {result.skipped.map(item => (
                  <Typography key={item.entityRef} variant="body2" color="textSecondary">
                    {item.entityRef}: {item.message}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Box>
        ) : (
          <>
            <Box display="flex" alignItems="flex-start" flexWrap="wrap" style={{ gap: 12 }}>
              <FormControl variant="outlined" size="small" style={{ flex: '1 1 220px', minWidth: 220 }}>
                <InputLabel id="batch-source-board-label">Catalog Jira board</InputLabel>
                <Select
                  labelId="batch-source-board-label"
                  label="Catalog Jira board"
                  value={sourceProjectKey}
                  onChange={event => handleSourceChange(event.target.value as string)}
                >
                  {sortedCatalogSources.map(summary => (
                    <MenuItem key={summary.projectKey} value={summary.projectKey}>
                      {summary.projectKey} ({summary.catalogComponentCount} component
                      {summary.catalogComponentCount === 1 ? '' : 's'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display="flex" alignItems="center" style={{ minHeight: 40 }}>
                <ArrowForwardIcon color="action" />
              </Box>

              <FormControl variant="outlined" size="small" style={{ flex: '1 1 220px', minWidth: 220 }}>
                <InputLabel id="batch-target-board-label">Target Jira project</InputLabel>
                <Select
                  labelId="batch-target-board-label"
                  label="Target Jira project"
                  value={targetProjectKey}
                  onChange={event => setTargetProjectKey(event.target.value as string)}
                  disabled={!sourceProjectKey}
                >
                  {sortedTargetProjects.map(project => (
                    <MenuItem key={project.key} value={project.key}>
                      {project.key} — {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {sourceProjectKey ? (
              <Box style={{ marginTop: 16 }}>
                <Typography variant="body2">
                  <strong>{updatableEntityRefs.length}</strong> component(s) will be included in
                  one batch job.
                  {skippedCount > 0 ? (
                    <>
                      {' '}
                      {skippedCount} component(s) are missing <code>github.com/project-slug</code>{' '}
                      and will be skipped.
                    </>
                  ) : null}
                </Typography>
              </Box>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default">
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result ? (
          <Button
            onClick={() => void handleSubmit()}
            color="primary"
            variant="contained"
            disabled={
              submitting ||
              sortedCatalogSources.length === 0 ||
              sortedTargetProjects.length === 0 ||
              updatableEntityRefs.length === 0
            }
          >
            {submitting ? 'Queueing…' : 'Queue batch job'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
